import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SongSummary } from "./SongSummary";
import { MultiSelectableList } from "../common/components/multiselectablelist/MultiSelectableList";
import { ItemsHeaderFirstRow } from "./ItemsHeaderFirstRow";
import { ItemsHeaderColumns } from "./ItemsHeaderColumns";
import { FuseResult } from "fuse.js";
import { SearchEngineEntry } from "../search/SearchEngineEntry";
import { RoutePaths } from "../RoutePaths";
import { useStateWithLocalStoragePersistenceAndDefault } from "../common/hooks/useStateWithLocalStoragePersistenceAndDefault";
import { LocalStorageKey } from "../localStoragePersistence/common/LocalStorageKey";
import { useSearchEngine } from "../search/useSearchEngine";
import { useSongsQuery } from "./useSongsQuery";
import { Column } from "../common/components/multiselectablelist/Column";
import { useSearchParams } from "react-router-dom";

import { useTranslation } from "react-i18next";

interface Props {
  bucket: string;
  setIsMainMenuOpen: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
}

const defaultEntryLimit = 50;

type Entry = SearchEngineEntry;

const keyPartOne = "ItemsTable";
const lskShowDetails = new LocalStorageKey(keyPartOne, "showDetails");
const lskOrderByField = new LocalStorageKey(keyPartOne, "orderByField");
const lskOrderByAscDesc = new LocalStorageKey(keyPartOne, "orderByAscOrDesc");
const lskEntryLimit = new LocalStorageKey(keyPartOne, "entryLimit");

export function ItemsTable({ bucket, setIsMainMenuOpen }: Props) {
  const { t } = useTranslation();

  const columns: Column<Entry>[] = useMemo(
    () => [
      { title: t("common.table.title"), field: "title" },
      { title: t("common.table.created"), field: "createdAt" },
      { title: t("common.table.updated"), field: "updatedAt" },
    ],
    [t],
  );

  const [showDetails, setShowDetails] =
    useStateWithLocalStoragePersistenceAndDefault<boolean>(
      lskShowDetails,
      true,
    );

  //TODO: extract to hook
  function changeOrderBy(newField: keyof Entry) {
    const newAscOrDesc =
      newField !== orderByField
        ? "asc"
        : orderByAscOrDesc === "asc"
          ? "desc"
          : "asc";
    setOrderByField(newField);
    setOrderByAscOrDesc(newAscOrDesc);
  }

  const [orderByField, setOrderByField] =
    useStateWithLocalStoragePersistenceAndDefault<keyof Entry>(
      lskOrderByField,
      "updatedAt",
    );
  const [orderByAscOrDesc, setOrderByAscOrDesc] =
    useStateWithLocalStoragePersistenceAndDefault<"asc" | "desc">(
      lskOrderByAscDesc,
      "desc",
    );

  const [entryLimit, setEntryLimit] =
    useStateWithLocalStoragePersistenceAndDefault<number>(
      lskEntryLimit,
      defaultEntryLimit,
    );

  const [searchParams, setSearchParams] = useSearchParams();

  const searchTerm = searchParams.get("q") ?? undefined;
  const selectedTonalities = useMemo(
    () =>
      searchParams
        .get("tonality")
        ?.split(",")
        .filter((v) => v) ?? [],
    [searchParams],
  );
  const selectedLanguages = useMemo(
    () =>
      searchParams
        .get("language")
        ?.split(",")
        .filter((v) => v) ?? [],
    [searchParams],
  );
  const selectedTags = useMemo(
    () =>
      searchParams
        .get("tag")
        ?.split(",")
        .filter((v) => v) ?? [],
    [searchParams],
  );

  const updateParams = useCallback(
    (
      updates:
        | Record<string, string | string[] | null>
        | ((prev: URLSearchParams) => Record<string, string | string[] | null>),
    ) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const actualUpdates =
            typeof updates === "function" ? updates(prev) : updates;
          Object.entries(actualUpdates).forEach(([key, value]) => {
            if (
              value === null ||
              (Array.isArray(value) && value.length === 0)
            ) {
              next.delete(key);
            } else if (Array.isArray(value)) {
              next.set(key, value.join(","));
            } else {
              next.set(key, value);
            }
          });
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setSearchTerm = useCallback(
    (val: string | undefined) =>
      updateParams({ q: val === undefined ? null : val }),
    [updateParams],
  );

  const setSelectedTonalities = useCallback(
    (val: string[] | ((prev: string[]) => string[])) => {
      updateParams((prev): { tonality: string[] } => {
        const current =
          prev
            .get("tonality")
            ?.split(",")
            .filter((v) => v) ?? [];
        const nextVal = typeof val === "function" ? val(current) : val;
        return { tonality: nextVal };
      });
    },
    [updateParams],
  );

  const setSelectedLanguages = useCallback(
    (val: string[] | ((prev: string[]) => string[])) => {
      updateParams((prev) => {
        const current =
          prev
            .get("language")
            ?.split(",")
            .filter((v) => v) ?? [];
        const nextVal = typeof val === "function" ? val(current) : val;
        return { language: nextVal };
      });
    },
    [updateParams],
  );

  const setSelectedTags = useCallback(
    (val: string[] | ((prev: string[]) => string[])) => {
      updateParams((prev) => {
        const current =
          prev
            .get("tag")
            ?.split(",")
            .filter((v) => v) ?? [];
        const nextVal = typeof val === "function" ? val(current) : val;
        return { tag: nextVal };
      });
    },
    [updateParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("q");
        next.delete("tonality");
        next.delete("language");
        next.delete("tag");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const { songs, fetching, reexecuteQuery } = useSongsQuery();
  const { search, allLanguages, allTonalities, allTags } = useSearchEngine(
    songs,
    entryLimit,
    selectedTonalities,
    selectedTags,
    selectedLanguages,
    bucket,
  );
  const [resultList, setResultList] = useState<FuseResult<Entry>[]>([]);

  const [selectedEntries, setSelectedEntries] = useState<Entry[]>([]);

  const itemsTableHeaderRef: React.RefObject<HTMLDivElement> =
    useRef<HTMLDivElement>(null);

  useEffect(() => {
    itemsTableHeaderRef.current?.scrollIntoView();
  }, [searchTerm, selectedTonalities, selectedLanguages, selectedTags]);

  useEffect(() => {
    const results = search(searchTerm, orderByField, orderByAscOrDesc);
    setResultList(results);
  }, [search, searchTerm, orderByField, orderByAscOrDesc]);

  const headerFirstRow = (
    <ItemsHeaderFirstRow
      columns={columns}
      changeOrderBy={changeOrderBy}
      showDetails={showDetails}
      orderByAscOrDesc={orderByAscOrDesc}
      orderByField={orderByField}
      setShowDetails={setShowDetails}
      entryLimit={entryLimit}
      setEntryLimit={setEntryLimit}
      setIsMainMenuOpen={setIsMainMenuOpen}
      allTonalities={allTonalities}
      selectedTonalities={selectedTonalities}
      setSelectedTonalities={setSelectedTonalities}
      allLanguages={allLanguages}
      selectedLanguages={selectedLanguages}
      setSelectedLanguages={setSelectedLanguages}
      allTags={allTags}
      selectedTags={selectedTags}
      setSelectedTags={setSelectedTags}
      searchTerm={searchTerm}
      clearFilters={clearFilters}
    />
  );

  const headerColumns = (
    <ItemsHeaderColumns<Entry>
      filterableColumn={columns[0]}
      additionalColumns={columns.slice(1)}
      changeOrderBy={changeOrderBy}
      orderByAscOrDesc={orderByAscOrDesc}
      orderByField={orderByField}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      selectedEntries={selectedEntries}
      bucket={bucket}
      reexecuteQuery={reexecuteQuery}
    />
  );

  function renderRow(entry: FuseResult<Entry>): ReactNode {
    return (
      <SongSummary
        key={entry.item.id}
        songSearchResult={entry}
        showDetails={showDetails}
      />
    );
  }

  return (
    <>
      <div ref={itemsTableHeaderRef} />

      <MultiSelectableList<FuseResult<Entry>>
        headerFirstRow={headerFirstRow}
        headerColumns={headerColumns}
        data={resultList}
        renderElement={renderRow}
        keyExtractor={keyExtractor}
        linkBuilder={linkBuilder}
        setSelectedEntries={(selected) =>
          setSelectedEntries(selected.map((s) => s.item))
        }
        loading={fetching}
      />
    </>
  );
}

const keyExtractor = (r: FuseResult<Entry>) => r.item.id;

const linkBuilder = (r: FuseResult<Entry>) =>
  RoutePaths.toItem(r.item.bucket, r.item.id);
